import React, { useState, useEffect, useRef } from "react";
import { Handle, Position, NodeProps, useReactFlow, useStore } from "reactflow";
import {
  FlaskConical,
  FunctionSquare,
  Trash2,
  Plus,
  Loader2,
} from "lucide-react";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import Confirmation from "@components/common/ConfirmDialog";
import { toast } from "@components/ui/use-toast";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import { executeWorkflow, getFunctions } from "../createPluginApis";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import ResponseViewer from "../common/ResponseViewer";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContentSection from "../common/NodeContentSection";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import { handlerStyle } from "../common/commonData";
import { useQuery } from "react-query";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import ExpressionEditor from "../common/ExpressionEditor";
import { flattenObject } from "../utils/fieldHelpers";
import CustomSelect from "@components/common/CustomSelect";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";
import LoadingProgressBar from "../common/LoadingProgressBar";
import { GeneralContext } from "@services/providers/GeneralProvider";

interface FunctionField {
  display_name: string;
  value: string;
  requires_input: boolean;
  data_type: string;
  is_custom?: boolean;
  is_required?: boolean;
}

interface FunctionDefinition {
  action: string;
  display_name: string;
  fields: FunctionField[];
  is_admin?: boolean | number | string;
}

interface ThreatLookupMapping {
  id: string;
  key: string;
  values: string;
  is_custom: boolean;
  data_type: string;
  display_name?: string;
  is_required?: boolean;
  enabled: boolean;
}

interface FunctionNodeData {
  label?: string;
  header?: string;
  uid: string;
  title: string;
  description?: string;
  action?: string;
  mappingDataList?: ThreatLookupMapping[];
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

const FunctionNode: React.FC<NodeProps<FunctionNodeData>> = ({ id, data }) => {
  const { isAdminUser } = React.useContext(GeneralContext);
  const label = data.label || data.header || "Function";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isTitleValid, setIsTitleValid] = useState(true);

  const hasAutoGeneratedMappings = useRef(false);
  const loadedParamsSignatureRef = useRef<string>("");
  const lastSyncedNodePayloadRef = useRef<string>("");
  const lastSyncedOutputRef = useRef<string>("");

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Action state
  const [action, setAction] = useState<string>(() => {
    const initialAction = data.action;
    return initialAction &&
      initialAction.trim() !== "" &&
      initialAction !== "none"
      ? initialAction
      : "none";
  });

  // Mappings state
  const [mappings, setMappings] = useState<ThreatLookupMapping[]>(
    data.mappingDataList || [],
  );

  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [visibleMappingCount, setVisibleMappingCount] = useState(0);

  const reactFlowInstance = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);

  // Ref to track previous outputData and prevent unnecessary updates
  const prevOutputDataRef = useRef<any>(null);

  const isNodeDragging = useStore(
    React.useCallback(
      (s) => (s.nodeInternals.get(id)?.dragging ?? false) as boolean,
      [id],
    ),
  );

  const connectedSourcesSignature = useStore(
    React.useCallback(
      (s) => {
        if (!isExpanded || isNodeDragging) return "";

        return s.edges
          .filter((e: any) => e.target === id)
          .map((e: any) => `${e.source}:${e.sourceHandle || ""}`)
          .sort()
          .join("|");
      },
      [id, isExpanded, isNodeDragging],
    ),
  );

  const sourceNodeDataSignature = useStore(
    React.useCallback(
      (s) => {
        if (!isExpanded || isNodeDragging) return "";

        const sourceIds = new Set(
          s.edges.filter((e: any) => e.target === id).map((e: any) => e.source),
        );

        if (sourceIds.size === 0) return "";

        return Array.from(s.nodeInternals.values())
          .filter((n: any) => sourceIds.has(n.id))
          .map((n: any) => {
            const nodeData: any = n.data || {};
            return [
              n.id,
              nodeData.executionStatus || "",
              nodeData.configData ? "cfg:1" : "cfg:0",
              nodeData.response,
              nodeData.outputData,
            ]
              .map((item) => {
                if (Array.isArray(item)) return `arr:${item.length}`;
                if (item && typeof item === "object") return "obj";
                return String(item ?? "");
              })
              .join(":");
          })
          .sort()
          .join("|");
      },
      [id, isExpanded, isNodeDragging],
    ),
  );

  const nodesForUi = React.useMemo(
    () => reactFlowInstance.getNodes(),
    [reactFlowInstance, sourceNodeDataSignature, connectedSourcesSignature],
  );

  const latestGraphRef = useRef<{ edges: any[]; nodes: any[] }>({
    edges: [],
    nodes: [],
  });

  // Fetch available functions from API
  const {
    data: functionsData,
    isFetched: areFunctionsFetched,
    isLoading: isFunctionsLoading,
  } = useQuery(["functions"], getFunctions, {
    staleTime: Infinity,
  });

  const availableFunctions: FunctionDefinition[] = functionsData?.data || [];

  // Get workflow type from active mesh flow
  const workflowType = React.useMemo(() => {
    try {
      const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
      if (activeMeshFlowStr) {
        const activeMeshFlow = JSON.parse(activeMeshFlowStr);
        return activeMeshFlow.type_workflow || "";
      }
    } catch (error) {
      console.error("Error parsing activeMeshFlow:", error);
    }
    return "";
  }, []);

  // Filter functions based on workflow type
  const filteredFunctions = React.useMemo(() => {
    const isThreatLookup =
      workflowType === "threatlookup" || workflowType === "threat_lookup";

    const permissionMatchedFunctions = availableFunctions.filter((func) => {
      if (func.is_admin === undefined || func.is_admin === null) {
        return true;
      }

      const isAdminRequired =
        func.is_admin === true ||
        func.is_admin === 1 ||
        func.is_admin === "1" ||
        func.is_admin === "true";

      return isAdminRequired === isAdminUser;
    });

    if (isThreatLookup) {
      return permissionMatchedFunctions.filter(
        (func) =>
          func.action === "threat_lookup" || func.action === "threatlookup",
      );
    } else {
      return permissionMatchedFunctions.filter(
        (func) =>
          func.action !== "threat_lookup" && func.action !== "threatlookup",
      );
    }
  }, [availableFunctions, workflowType, isAdminUser]);

  // Reset action if it is not available after permission/workflow filtering
  useEffect(() => {
    // Wait for function definitions to load before validating prefilled action.
    // Without this guard, initial load can incorrectly reset saved action to "none".
    if (!areFunctionsFetched) {
      return;
    }

    if (action === "none") {
      return;
    }

    const actionExists = filteredFunctions.some(
      (func) => func.action === action,
    );
    if (!actionExists) {
      setAction("none");
    }
  }, [filteredFunctions, action, areFunctionsFetched]);

  // Reset action to "none" if threat_lookup is selected but workflow type is not threat_lookup
  useEffect(() => {
    const isThreatLookup =
      workflowType === "threatlookup" || workflowType === "threat_lookup";
    const isThreatLookupAction =
      action === "threat_lookup" || action === "threatlookup";

    if (!isThreatLookup && isThreatLookupAction) {
      setAction("none");
    }
  }, [workflowType, action]);

  // Get current function definition based on selected action
  const currentFunctionDef = filteredFunctions.find(
    (func) => func.action === action,
  );

  // Check if all required fields have values and all custom fields have keys
  const areRequiredFieldsFilled = React.useMemo(() => {
    const enabledMappings = mappings.filter((m) => m.enabled);
    const requiredMappings = enabledMappings.filter((m) => m.is_required);
    const customMappings = enabledMappings.filter((m) => m.is_custom);

    const requiredFieldsValid = requiredMappings.every((m) => m.values?.trim());
    const customFieldsValid = customMappings.every((m) => m.key?.trim());

    const mappingKeys = enabledMappings
      .map((m) => m.key.toLowerCase())
      .filter((key) => key.trim() !== "");
    const hasDuplicates = mappingKeys.length !== new Set(mappingKeys).size;

    const validMappings = enabledMappings.filter((m) => m.key?.trim());
    const hasMappingsForThreatLookup =
      action !== "threat_lookup" || validMappings.length > 0;

    return (
      requiredFieldsValid &&
      customFieldsValid &&
      !hasDuplicates &&
      hasMappingsForThreatLookup
    );
  }, [mappings, action]);

  // Copy output to clipboard
  const handleCopyOutput = () => {
    const dataToCopy = errorResponse || outputData;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: errorResponse
        ? "Error copied to clipboard."
        : "Output copied to clipboard.",
      duration: 2000,
    });
  };

  // Lightweight input comparison to avoid repeated deep JSON serializations
  const areInputsEqual = React.useCallback((a: any[], b: any[]) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i += 1) {
      const left = a[i];
      const right = b[i];

      if (left === right) continue;
      if (!left || !right) return false;

      if (
        left.nodeType !== right.nodeType ||
        left.label !== right.label ||
        left.header !== right.header ||
        left.configData !== right.configData ||
        left.response !== right.response ||
        left.outputData !== right.outputData
      ) {
        return false;
      }
    }

    return true;
  }, []);

  const areMappingsEqual = React.useCallback(
    (a: ThreatLookupMapping[], b: ThreatLookupMapping[]) => {
      if (a === b) return true;
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;

      for (let i = 0; i < a.length; i += 1) {
        const left = a[i];
        const right = b[i];
        if (
          left.id !== right.id ||
          left.key !== right.key ||
          left.values !== right.values ||
          left.is_custom !== right.is_custom ||
          left.data_type !== right.data_type ||
          left.display_name !== right.display_name ||
          left.is_required !== right.is_required ||
          left.enabled !== right.enabled
        ) {
          return false;
        }
      }

      return true;
    },
    [],
  );

  // Manual refresh function for InputViewer
  const updateInputDataIfChanged = React.useCallback(
    (nextInputs: any[]) => {
      setInputData((currentInputData) => {
        return areInputsEqual(currentInputData || [], nextInputs || [])
          ? currentInputData
          : nextInputs;
      });
    },
    [areInputsEqual],
  );

  const refreshInputData = React.useCallback(() => {
    const latestEdges = reactFlowInstance.getEdges();
    const latestNodes = reactFlowInstance.getNodes();
    latestGraphRef.current = {
      edges: latestEdges as any[],
      nodes: latestNodes as any[],
    };

    const nextInputs = getConnectedInputs(
      id,
      latestEdges as any,
      latestNodes as any,
    );
    updateInputDataIfChanged(nextInputs);
  }, [id, updateInputDataIfChanged, reactFlowInstance]);
  useEffect(() => {
    if (!isExpanded && inputData.length > 0) return;

    if (isNodeDragging) return;
    refreshInputData();
  }, [
    isExpanded,
    isNodeDragging,
    connectedSourcesSignature,
    sourceNodeDataSignature,
    inputData.length,
    refreshInputData,
  ]);

  // Refresh once immediately when expanded
  useEffect(() => {
    if (!isExpanded) return;
    refreshInputData();
  }, [isExpanded, refreshInputData]);

  // Expand shell first, then hydrate heavy section content on next paint
  useEffect(() => {
    if (!isExpanded) {
      setIsContentReady(false);
      return;
    }

    let raf2: number | null = null;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setIsContentReady(true);
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isExpanded]);

  // Sync inputData from data.inputData prop (set during execution)
  useEffect(() => {
    const propInputData = (data as any).inputData;

    if (
      !propInputData ||
      !Array.isArray(propInputData) ||
      propInputData.length === 0
    ) {
      return;
    }

    setInputData((currentInputData) => {
      if (!areInputsEqual(propInputData, currentInputData || [])) {
        return propInputData;
      }
      return currentInputData;
    });
  }, [id, (data as any).inputData, areInputsEqual]);

  // Sync outputData from node data (updated during workflow/node execution)
  useEffect(() => {
    const newOutputData = (data as any).outputData;

    if (!newOutputData) {
      if (outputData !== null && newOutputData === null) {
        setOutputData(null);
      }
      return;
    }

    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;
      setOutputData(newOutputData);
    } else {
      prevOutputDataRef.current = newOutputData;
    }
  }, [(data as any).outputData]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  // FIX 1: Sync metadata changes with parent — outputData removed from deps
  // to break the re-render loop (outputData change → onDataChange → new data
  // prop → effects fire again).
  useEffect(() => {
    if (!data.onDataChange) return;

    const actionValue = action === "none" ? "" : action;

    const transformedMappings = (mappings || [])
      .filter((m) => m.enabled && m.key?.trim())
      .map((m) => ({
        key: m.key,
        values: m.values,
        is_custom: m.is_custom,
        data_type: m.data_type,
        display_name: m.display_name,
        is_required: m.is_required,
      }));

    const storedParameters = {
      action: actionValue,
      mappingDataList: transformedMappings,
    };

    const nextPayloadSignature = JSON.stringify({
      uid,
      title,
      description,
      action: actionValue,
      storedParameters,
    });

    if (lastSyncedNodePayloadRef.current === nextPayloadSignature) {
      return;
    }
    lastSyncedNodePayloadRef.current = nextPayloadSignature;

    data.onDataChange(id, {
      uid,
      title,
      description,
      action: actionValue,
      mappingDataList: mappings,
      mappingThreatLookup: undefined,
      storedParameters,
      parameters: storedParameters,
    });
  }, [uid, title, description, action, mappings, id]);
  // outputData intentionally excluded — synced separately below

  // FIX 1 (cont): Sync outputData separately so it doesn't trigger full
  // metadata re-sync on every execution result change.
  useEffect(() => {
    if (!data.onDataChange) return;

    const nextOutputSignature = JSON.stringify(outputData);
    if (lastSyncedOutputRef.current === nextOutputSignature) return;
    lastSyncedOutputRef.current = nextOutputSignature;

    data.onDataChange(id, {
      outputData,
      response: outputData,
    });
  }, [outputData, id]);

  // Generate UID on mount
  useEffect(() => {
    if (!uid) {
      const newUid = `funcion_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      setUid(newUid);
    }
  }, [uid]);

  // Load saved parameters from data prop (when loading/switching workflows)
  useEffect(() => {
    const params =
      (data as any).storedParameters || (data as any).parameters || data || {};

    const incomingAction =
      params.action !== undefined ? params.action : (data as any).action;
    const normalizedAction =
      typeof incomingAction === "string" &&
      incomingAction.trim() !== "" &&
      incomingAction.trim().toLowerCase() !== "none"
        ? incomingAction
        : "none";
    const rawMappings =
      params.mappingDataList ||
      params.mappingThreatLookup ||
      (data as any).mappingDataList ||
      (data as any).mappingThreatLookup ||
      [];

    const incomingSignature = JSON.stringify({
      action: normalizedAction,
      mappings: Array.isArray(rawMappings)
        ? rawMappings.map((m: any) => ({
            key: m?.key || "",
            values: m?.values || "",
            is_custom: m?.is_custom !== undefined ? m.is_custom : false,
            // FIX 2: normalize data_type with fallback on load
            data_type: m?.data_type || "string",
            display_name: m?.display_name || "",
            is_required: !!m?.is_required,
            enabled: m?.enabled !== false,
          }))
        : [],
    });

    if (loadedParamsSignatureRef.current === incomingSignature) {
      return;
    }
    loadedParamsSignatureRef.current = incomingSignature;

    setAction((prev) => (prev === normalizedAction ? prev : normalizedAction));

    if (Array.isArray(rawMappings) && rawMappings.length > 0) {
      const loadedMappings = rawMappings.map((m: any, index: number) => {
        // Try to find the correct data_type from the live function definition
        const fieldDef = currentFunctionDef?.fields?.find(
          (f) => f.value === m.key,
        );
        const resolvedDataType = m.data_type || fieldDef?.data_type || "string";

        return {
          id:
            m.id ||
            `mapping_${(m.key || "field").toString().toLowerCase()}_${index}`,
          key: m.key || "",
          values: m.values || "",
          is_custom: m.is_custom !== undefined ? m.is_custom : false,
          data_type: resolvedDataType,
          display_name: m.display_name || fieldDef?.display_name || "",
          is_required: m.is_required || fieldDef?.is_required || false,
          enabled: m.enabled !== false,
        };
      });
      setMappings((prevMappings) => {
        if (areMappingsEqual(prevMappings, loadedMappings)) {
          return prevMappings;
        }
        return loadedMappings;
      });
    } else {
      setMappings((prevMappings) =>
        prevMappings.length === 0 ? prevMappings : [],
      );
    }
  }, [id, data, areMappingsEqual]);

  // FIX 3: renamed internal field-map builder to buildInputFieldsMap to avoid
  // shadowing the imported flattenObject from fieldHelpers. The local version
  // populates a closure-scoped Map rather than pushing to an array, so they
  // can't share an implementation — but the name clash caused the import to be
  // silently ignored in getAvailableFields, making autocomplete miss fields.
  const generateMappingsFromFunctionDef = (
    functionDef: FunctionDefinition,
    inputDataObj: any,
  ): ThreatLookupMapping[] => {
    if (!functionDef || !functionDef.fields) return [];

    const timestamp = Date.now();
    const generatedMappings: ThreatLookupMapping[] = [];

    const inputFieldsMap = new Map<string, string>();

    const maxScannedFields = 200;

    const buildInputFieldsMap = (obj: any, prefix: string = "") => {
      if (inputFieldsMap.size >= maxScannedFields) return;

      Object.entries(obj).forEach(([key, value]) => {
        if (inputFieldsMap.size >= maxScannedFields) return;

        const fieldPath = prefix ? `${prefix}.${key}` : key;
        const fullPath = `{{$json.${fieldPath}}}`;

        inputFieldsMap.set(key.toLowerCase(), fullPath);
        inputFieldsMap.set(fieldPath.toLowerCase(), fullPath);

        if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          buildInputFieldsMap(value, fieldPath);
        }
      });
    };

    if (Array.isArray(inputDataObj) && inputDataObj.length > 0) {
      buildInputFieldsMap(inputDataObj[0]);
    } else if (typeof inputDataObj === "object" && inputDataObj !== null) {
      buildInputFieldsMap(inputDataObj);
    }

    functionDef.fields
      .filter((field) => !field.is_custom)
      .forEach((field, index) => {
        let matchedValue = "";

        const fieldValueLower = field.value.toLowerCase();
        if (inputFieldsMap.has(fieldValueLower)) {
          matchedValue = inputFieldsMap.get(fieldValueLower) || "";
        }

        if (!matchedValue) {
          const normalizedFieldValue = fieldValueLower.replace(/_/g, "");
          for (const [key, path] of inputFieldsMap.entries()) {
            const normalizedKey = key.replace(/_/g, "");
            if (normalizedKey === normalizedFieldValue) {
              matchedValue = path;
              break;
            }
          }
        }

        generatedMappings.push({
          id: `${timestamp}_${index}`,
          key: field.value,
          values: matchedValue,
          is_custom: false,
          // FIX 2: always fall back to "string" so the Select/read-only
          // display always has a valid value even if the API omits data_type.
          data_type: field.data_type || "string",
          display_name: field.display_name,
          is_required: field.is_required,
          enabled: true,
        });
      });

    return generatedMappings;
  };

  // Execute function node
  const executeFunctionNode = async () => {
    const enabledMappings = mappings.filter((m) => m.enabled && m.key.trim());

    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required for the function node",
        duration: 3000,
      });
      return;
    }

    if (!action || action === "none") {
      toast({
        variant: "destructive",
        title: "No Action Selected",
        description: "Please select an action type",
        duration: 3000,
      });
      return;
    }

    if (enabledMappings.length === 0) {
      toast({
        variant: "destructive",
        title: "No Mappings Configured",
        description: "Configure at least one field mapping",
        duration: 3000,
      });
      return;
    }

    const missingRequiredFields = enabledMappings.filter(
      (m) => m.is_required && !m.values?.trim(),
    );

    if (missingRequiredFields.length > 0) {
      const missingFieldNames = missingRequiredFields
        .map((m) => m.display_name || m.key)
        .join(", ");
      toast({
        variant: "destructive",
        title: "Missing Required Fields",
        description: `Please provide values for: ${missingFieldNames}`,
        duration: 4000,
      });
      return;
    }

    const customFieldsWithoutKey = enabledMappings.filter(
      (m) => m.is_custom && !m.key?.trim(),
    );

    if (customFieldsWithoutKey.length > 0) {
      toast({
        variant: "destructive",
        title: "Missing Keys",
        description: "All custom fields must have a key",
        duration: 4000,
      });
      return;
    }

    const mappingKeys = enabledMappings.map((m) => m.key.toLowerCase());
    const duplicates = mappingKeys.filter(
      (key, index) => mappingKeys.indexOf(key) !== index,
    );

    if (duplicates.length > 0) {
      toast({
        variant: "destructive",
        title: "Duplicate Keys",
        description: `Key "${duplicates[0]}" is configured multiple times`,
        duration: 3000,
      });
      return;
    }

    const pluginId = sessionStorage.getItem("editPluginId");

    if (!pluginId || pluginId === "temp-plugin") {
      toast({
        variant: "destructive",
        title: "Plugin Not Saved",
        description: "Please save the plugin first before testing",
        duration: 4000,
      });
      return;
    }

    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setOutputData(null);
    setErrorResponse(null);
    setLoading(true);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);
      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (isExecutable) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "pending",
                executionResult: undefined,
                error: undefined,
              },
            };
          }
          return node;
        }),
      );

      const workflowNodes = executableNodes.map((node: any) => {
        let nodeType = "core";
        let subType = "http_request";
        let parameters;

        if (node.id === id) {
          const enabledMappings = mappings.filter(
            (m) => m.enabled && m.key.trim(),
          );
          const transformedMappings = enabledMappings.map((m) => ({
            key: m.key,
            values: m.values,
            is_custom: m.is_custom,
            data_type: m.data_type,
            display_name: m.display_name,
            is_required: m.is_required,
          }));

          const functionParameters: any = {
            action,
            mappingDataList: transformedMappings,
          };

          nodeType = "function_node";
          subType = "functional_node_linkshadow";
          parameters = {
            ...functionParameters,
            label: label,
          };
        } else {
          const backendTypeInfo = getNodeBackendType(node.type || "custom");
          nodeType = backendTypeInfo.type;
          subType = backendTypeInfo.sub_type;

          parameters = getNodeExecutionParameters(node, pluginId).parameters;
        }

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.label || "Untitled",
          description: node.data.description || "",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      const nodeIdToUidMap: Record<string, string> = {};
      executableNodes.forEach((node: any) => {
        nodeIdToUidMap[node.id] = node.data.uid || node.id;
      });

      const executionPathNodeIds = new Set(
        executableNodes.map((n: any) => n.id),
      );

      const connections: any = {};
      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeIdToUidMap[edge.source];
          const targetUid = nodeIdToUidMap[edge.target];

          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = {
              main: [],
            };
          }

          const sourceNode = executableNodes.find(
            (n: any) => n.id === edge.source,
          );
          const isIfElseNode =
            sourceNode?.type === "ifElseNode" ||
            sourceNode?.data?.sub_type === "if_else";

          let outputIndex = 0;
          if (isIfElseNode && edge.sourceHandle) {
            outputIndex = edge.sourceHandle === "false" ? 1 : 0;
          }

          if (!connections[sourceUid].main[outputIndex]) {
            connections[sourceUid].main[outputIndex] = [];
          }

          connections[sourceUid].main[outputIndex].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const result = await executeWorkflow(workflowPayload);

      if (result?.results?.[0]?.node_results) {
        const nodeResults = result.results[0].node_results;
        const allEdges = reactFlowInstance.getEdges();

        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeUid = uid || id;
        const currentNodeResult = resultsMap.get(currentNodeUid);
        if (currentNodeResult) {
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setOutputData(null);
          } else if (currentNodeResult.output_data) {
            const extractData = (data: any) => {
              if (Array.isArray(data)) {
                return data.map((item: any) => item?.json_data || item);
              }
              return data?.json_data || data;
            };

            const unwrappedCurrentOutput = extractData(
              currentNodeResult.output_data,
            );
            setOutputData(unwrappedCurrentOutput);
            setErrorResponse(null);
          }
        }

        if (hasErrors) {
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          toast({
            variant: "success",
            title: "Success",
            description: "Function executed successfully",
            duration: 3000,
          });
        }
      } else {
        const isSuccess =
          result?.success === true ||
          result?.status === "success" ||
          result?.body?.status === 1 ||
          result?.body?.status === "1" ||
          result?.status === 1;

        if (isSuccess) {
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          setErrorResponse(null);

          toast({
            variant: "success",
            title: "Success",
            description: result?.message || "Function executed successfully",
            duration: 3000,
          });
        } else {
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          setErrorResponse({
            error:
              result?.body?.message || result?.message || "Execution failed",
            response: result,
          });
          setOutputData(null);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      result?.body?.message ||
                      result?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              result?.body?.message ||
              result?.message ||
              "Function execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Function Execution Error:", error);

      setErrorResponse({
        error: error?.message || "Function execution failed",
        details:
          error?.response?.data ||
          error?.stack ||
          "No additional details available",
      });
      setOutputData(null);

      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Function execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Function execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    data.onDelete?.(id);
    setShowDeleteConfirm(false);
  };

  const removeMapping = (mappingId: string) => {
    setMappings((prevMappings) => {
      const updatedMappings = prevMappings.filter((m) => m.id !== mappingId);

      if (updatedMappings.length === 0) {
        setAction("none");
      }

      return updatedMappings;
    });
  };

  const updateMapping = (
    mappingId: string,
    updates: Partial<ThreatLookupMapping>,
  ) => {
    let hasDuplicateKey = false;

    setMappings((prevMappings) => {
      if (updates.key !== undefined) {
        const newKey = updates.key.trim().toLowerCase();
        const isDuplicate = prevMappings.some(
          (m) =>
            m.id !== mappingId &&
            m.key.toLowerCase() === newKey &&
            newKey !== "",
        );
        if (isDuplicate) {
          hasDuplicateKey = true;
          return prevMappings;
        }
      }

      // When value changes, auto-set data_type from the field definition
      // keyed by this mapping's key — source of truth is the function schema,
      // not what the user typed.
      if (updates.values !== undefined && updates.data_type === undefined) {
        const mapping = prevMappings.find((m) => m.id === mappingId);
        if (mapping) {
          const fieldDef = currentFunctionDef?.fields?.find(
            (f) => f.value === mapping.key,
          );
          if (fieldDef?.data_type) {
            updates = { ...updates, data_type: fieldDef.data_type };
          }
        }
      }

      return prevMappings.map((m) =>
        m.id === mappingId ? { ...m, ...updates } : m,
      );
    });

    if (hasDuplicateKey) {
      toast({
        variant: "destructive",
        title: "Duplicate Key",
        description: `Key "${updates.key}" already exists. Please use a unique key.`,
        duration: 3000,
      });
    }
  };

  const addCustomMapping = () => {
    const newMapping: ThreatLookupMapping = {
      id: `custom_${Date.now()}`,
      key: "",
      values: "",
      is_custom: true,
      // FIX 2: new custom mappings always start with "string" so the
      // Select renders with a valid default value immediately.
      data_type: "string",
      display_name: "",
      is_required: false,
      enabled: true,
    };
    setMappings((prevMappings) => [...prevMappings, newMapping]);
  };

  const getDisplayData = React.useCallback((input: any) => {
    const EXCLUDE = [
      "uid",
      "isDecript",
      "isDecrypted",
      "title",
      "testConnectionBtn",
      "tabId",
      "configType",
      "onDelete",
      "onDataChange",
      "header",
      "borderColor",
      "shadowColor",
      "label",
      "nodeType",
      "itemCount",
    ];

    const extractJsonData = (data: any) => {
      if (Array.isArray(data)) {
        return data.map((item) => {
          if (item && typeof item === "object" && "json_data" in item) {
            return item.json_data;
          }
          return item;
        });
      }
      if (data && typeof data === "object" && "json_data" in data) {
        return data.json_data;
      }
      return data;
    };

    if (input.outputData) {
      return extractJsonData(input.outputData);
    }

    if (input.response) {
      return extractJsonData(input.response);
    }

    const displayData: any = {};
    if (input.configData) {
      const encryptedFields = input.configData.isDecript || [];

      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE.includes(key))
        .forEach(([key, value]) => {
          if (Array.isArray(encryptedFields) && encryptedFields.includes(key)) {
            displayData[key] = "********************";
          } else {
            displayData[key] = value;
          }
        });
    }
    return displayData;
  }, []);

  const firstInputDisplayData = React.useMemo(() => {
    if (!inputData?.length) return null;
    return getDisplayData(inputData[0]);
  }, [inputData, getDisplayData]);

  const actionOptions = React.useMemo(
    () => [
      { label: "Select an action", value: "none" },
      ...(filteredFunctions.length > 0
        ? filteredFunctions.map((func) => ({
            label: func.display_name,
            value: func.action,
          }))
        : [
            {
              label: "Threat Lookup",
              value: "threat_lookup",
            },
          ]),
    ],
    [filteredFunctions],
  );

  const validationHint = React.useMemo(() => {
    if (areRequiredFieldsFilled && action && action !== "none") {
      return "";
    }

    if (!action || action === "none") {
      return "Select an action type to enable execution";
    }

    const enabledMappings = mappings.filter((m) => m.enabled);
    const validMappings = enabledMappings.filter((m) => m.key?.trim());

    if (action === "threat_lookup" && validMappings.length === 0) {
      return "At least one field mapping is required for threat lookup";
    }

    const mappingKeys = enabledMappings
      .map((m) => m.key.toLowerCase())
      .filter((key) => key.trim() !== "");
    const hasDuplicates = mappingKeys.length !== new Set(mappingKeys).size;

    if (hasDuplicates) {
      return "Duplicate keys detected. All field keys must be unique.";
    }

    if (mappings.some((m) => m.is_custom && !m.key?.trim())) {
      return "Fill all custom field keys to enable execution";
    }

    return "Fill all required fields (*) to enable execution";
  }, [areRequiredFieldsFilled, action, mappings]);

  // FIX 3: uses the imported flattenObject from fieldHelpers (no local
  // shadowing) so autocomplete fields are correctly populated.
  const getAvailableFields = React.useMemo((): Array<{
    path: string;
    value: any;
  }> => {
    if (!isExpanded || !isContentReady) return [];

    const fields: Array<{ path: string; value: any }> = [];

    if (inputData && inputData.length > 0 && firstInputDisplayData) {
      const firstInput = inputData[0];
      const prefix = firstInput.configData ? "config" : "$json";
      flattenObject(firstInputDisplayData, prefix, fields);
    }

    return fields;
  }, [inputData, isExpanded, isContentReady, firstInputDisplayData]);

  const availableFieldTypeByPath = React.useMemo(() => {
    const typeMap = new Map<string, ThreatLookupMapping["data_type"]>();

    const getTypeFromValue = (value: any): ThreatLookupMapping["data_type"] => {
      if (Array.isArray(value)) return "array";
      if (value === null || value === undefined) return "string";
      if (typeof value === "boolean") return "boolean";
      if (typeof value === "number") {
        return Number.isInteger(value) ? "integer" : "string";
      }
      if (typeof value === "object") return "object";
      return "string";
    };

    getAvailableFields.forEach((field) => {
      const normalizedPath = field.path
        .replace(/^\$json\[\d+\]\./, "")
        .replace(/^\$json/, "")
        .trim();

      if (!normalizedPath) return;
      typeMap.set(normalizedPath, getTypeFromValue(field.value));
    });

    return typeMap;
  }, [getAvailableFields]);

  const duplicateKeySet = React.useMemo(() => {
    const countMap = new Map<string, number>();

    mappings.forEach((mapping) => {
      const normalizedKey = mapping.key?.trim().toLowerCase();
      if (!normalizedKey) return;
      countMap.set(normalizedKey, (countMap.get(normalizedKey) || 0) + 1);
    });

    const duplicates = new Set<string>();
    countMap.forEach((count, key) => {
      if (count > 1) duplicates.add(key);
    });

    return duplicates;
  }, [mappings]);

  const inputViewerSources = React.useMemo(() => {
    return inputData.map((input, index) => {
      let displayLabel = input.label || `Source ${index + 1}`;

      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData,
      };
    });
  }, [inputData, getDisplayData]);

  const renderInputData = () => {
    if (!inputData?.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }

    return (
      <PaginatedInputViewer
        sources={inputViewerSources}
        onRefresh={refreshInputData}
        sourceCount={inputData.length}
        height="530px"
        draggable={true}
        dragDataFormat="expression"
      />
    );
  };

  useEffect(() => {
    if (!isContentReady) {
      if (visibleMappingCount !== 0) {
        setVisibleMappingCount(0);
      }
      return;
    }

    if (mappings.length === 0) {
      if (visibleMappingCount !== 0) {
        setVisibleMappingCount(0);
      }
      return;
    }

    // Render an initial chunk immediately so fullscreen/open feels responsive.
    if (visibleMappingCount === 0) {
      const INITIAL_BATCH_SIZE = 10;
      setVisibleMappingCount(Math.min(INITIAL_BATCH_SIZE, mappings.length));
      return;
    }

    if (visibleMappingCount >= mappings.length) return;

    const remaining = mappings.length - visibleMappingCount;
    const BATCH_SIZE = remaining > 40 ? 10 : remaining > 20 ? 6 : 3;
    const FALLBACK_DELAY_MS = 20;
    const win = window as any;

    if (typeof win.requestIdleCallback === "function") {
      const idleId = win.requestIdleCallback(
        () => {
          setVisibleMappingCount((n) =>
            Math.min(n + BATCH_SIZE, mappings.length),
          );
        },
        { timeout: 80 },
      );

      return () => {
        if (typeof win.cancelIdleCallback === "function") {
          win.cancelIdleCallback(idleId);
        }
      };
    }

    const timeoutId = window.setTimeout(() => {
      setVisibleMappingCount((n) => Math.min(n + BATCH_SIZE, mappings.length));
    }, FALLBACK_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isContentReady, visibleMappingCount, mappings.length]);

  const isMappingsLoadingInParameters =
    mappings.length > 0 && visibleMappingCount < mappings.length;

  const prevMappingsLength = useRef(0);

  useEffect(() => {
    if (
      action &&
      action !== "" &&
      prevMappingsLength.current > 0 &&
      mappings.length === 0
    ) {
      setAction("");
    }

    prevMappingsLength.current = mappings.length;
  }, [mappings, action]);

  React.useEffect(() => {
    if (
      hasAutoGeneratedMappings.current ||
      mappings.length > 0 ||
      !currentFunctionDef ||
      !inputData?.length
    ) {
      return;
    }

    const autoMappings = generateMappingsFromFunctionDef(
      currentFunctionDef,
      firstInputDisplayData,
    );

    if (autoMappings.length > 0) {
      setMappings((prev) => {
        const existingMap = new Map(prev.map((m) => [m.key, m]));

        return autoMappings.map((auto) => {
          const existing = existingMap.get(auto.key);

          return existing
            ? {
                ...auto,
                ...existing,
              }
            : auto;
        });
      });
      hasAutoGeneratedMappings.current = true;
    }
  }, [inputData, currentFunctionDef, firstInputDisplayData]);

  const handleActionSelect = React.useCallback(
    (value: string) => {
      setAction(value);

      if (value === "none" || value === "") {
        setMappings([]);
        prevMappingsLength.current = 0;
        hasAutoGeneratedMappings.current = false;
        return;
      }

      const selectedFunction = filteredFunctions.find(
        (func) => func.action === value,
      );
      if (!selectedFunction || !firstInputDisplayData) return;

      const autoMappings = generateMappingsFromFunctionDef(
        selectedFunction,
        firstInputDisplayData,
      );
      if (autoMappings.length === 0) return;

      setMappings((prev) => {
        const existingMap = new Map(prev.map((m) => [m.key, m]));
        return autoMappings.map((auto) => {
          const existing = existingMap.get(auto.key);
          return existing ? { ...auto, ...existing } : auto;
        });
      });

      hasAutoGeneratedMappings.current = true;
    },
    [filteredFunctions, firstInputDisplayData],
  );

  const openRaf1Ref = useRef<number | null>(null);
  const openRaf2Ref = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpening) return undefined;

    if (isFunctionsLoading) {
      return undefined;
    }

    openRaf1Ref.current = requestAnimationFrame(() => {
      openRaf2Ref.current = requestAnimationFrame(() => {
        setIsExpanded(true);
        setIsOpening(false);
      });
    });

    return () => {
      if (openRaf1Ref.current) cancelAnimationFrame(openRaf1Ref.current);
      if (openRaf2Ref.current) cancelAnimationFrame(openRaf2Ref.current);
    };
  }, [isOpening, isFunctionsLoading]);

  const handleToggleExpand = React.useCallback(() => {
    if (isExpanded) {
      setIsExpanded(false);
      setIsOpening(false);
      return;
    }
    if (isOpening) return;
    setIsOpening(true);
  }, [isExpanded, isOpening]);

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, handleToggleExpand),
          createContextMenuItems.delete(() => handleDelete(), !!data.onDelete),
        ]}
      />

      {/* Handles */}
      <Handle type="target" position={Position.Left} className={handlerStyle} />
      <Handle
        type="source"
        position={Position.Right}
        className={handlerStyle}
      />

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-[padding] duration-300 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            isOpening ? (
              <Loader2 className="animate-spin" />
            ) : (
              <FunctionSquare
                className={
                  (data as any).executionStatus === "running" ||
                  (data as any).executionStatus === "pending"
                    ? "animate-bounce"
                    : ""
                }
              />
            )
          }
          label={isOpening ? "Loading..." : label}
          title={title}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={handleToggleExpand}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Metadata Form */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodesForUi}
                dataTitle={data.title}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={1}
              />
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
            style={{ contain: "layout style" }}
          >
            {/* Left Section - Input Data */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                {!isContentReady ? (
                  <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center w-[200px]">
                    <div className="font-medium">Loading Input</div>
                    <div>Preparing connected data...</div>
                  </div>
                ) : (
                  renderInputData()
                )}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Function Configuration */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                <>
                  <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2">
                    <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                      Parameters
                    </h3>

                    {!isContentReady ? (
                      <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center mt-2">
                        Loading parameters...
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Action Selection */}
                        <div className="space-y-2 border rounded-md p-3 bg-muted/20 mt-2">
                          <label className="text-xs font-semibold">
                            Action Type
                          </label>
                          <CustomSelect
                            data={actionOptions}
                            defaultValue={action}
                            onSelect={handleActionSelect}
                            placeholder="Select action type"
                            multiSelect={false}
                          />
                          <p className="text-[10px] text-muted-foreground">
                            Select the type of function to execute
                          </p>
                        </div>

                        {/* Function Mappings Section */}
                        <div className="space-y-2 border rounded-md p-3 bg-muted/10">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-semibold">
                              {currentFunctionDef?.display_name || "Function"}{" "}
                              Mappings
                            </label>
                            {isMappingsLoadingInParameters && (
                              <div className="flex items-center gap-1.5 text-[10px] font-medium text-primary bg-primary/10 border border-primary/20 px-2 py-1 rounded">
                                <Loader2 size={12} className="animate-spin" />
                                <span>
                                  Loading {visibleMappingCount}/
                                  {mappings.length}
                                </span>
                              </div>
                            )}
                          </div>

                          {isMappingsLoadingInParameters && (
                            <LoadingProgressBar
                              current={visibleMappingCount}
                              total={mappings.length}
                              label="Preparing mappings..."
                              sticky={true}
                            />
                          )}

                          {mappings.length === 0 ? (
                            <div className="border-2 border-dashed rounded-md p-4 text-center bg-background/50">
                              <FunctionSquare
                                size={32}
                                className="mx-auto mb-2 text-muted-foreground"
                              />
                              <p className="text-xs text-muted-foreground">
                                Connect an input node to auto-populate mappings
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-2 max-h-full overflow-y-auto">
                              {mappings
                                .slice(0, visibleMappingCount)
                                .map((mapping) => {
                                  const isCustomField =
                                    mapping.is_custom === true;
                                  const normalizedMappingKey =
                                    mapping.key?.trim().toLowerCase() || "";
                                  const isDuplicateCustomKey =
                                    !!normalizedMappingKey &&
                                    isCustomField &&
                                    duplicateKeySet.has(normalizedMappingKey);

                                  return (
                                    <div
                                      key={mapping.id}
                                      className="border shadow-md rounded-md bg-muted/20 space-y-2 p-3"
                                    >
                                      {/* Mapping Header */}
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0 border-b pb-1 w-full">
                                          <div className="text-xs font-semibold truncate text-primary flex items-center gap-1">
                                            {mapping.display_name ||
                                              mapping.key ||
                                              "(empty key)"}
                                            {mapping.is_required && (
                                              <span className="text-red-500 ml-1">
                                                *
                                              </span>
                                            )}
                                            {isCustomField && (
                                              <span className="text-[10px] bg-lightGreen text-primaryGreen px-1.5 py-0.5 rounded">
                                                Custom
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <Trash2
                                          size={14}
                                          className="cursor-pointer text-primary hover:text-primary/80 flex-shrink-0"
                                          onClick={() =>
                                            removeMapping(mapping.id)
                                          }
                                        />
                                      </div>

                                      {/* Key Field */}
                                      <div className="space-y-1">
                                        <label className="text-xs text-muted-foreground">
                                          Key{" "}
                                          {(isCustomField ||
                                            mapping.is_required) && (
                                            <span className="text-red-500">
                                              *
                                            </span>
                                          )}
                                        </label>
                                        {isCustomField ? (
                                          <>
                                            <Input
                                              value={mapping.key}
                                              onChange={(e) =>
                                                updateMapping(mapping.id, {
                                                  key: e.target.value,
                                                })
                                              }
                                              placeholder="Enter field key"
                                              className={`text-xs h-8 ${
                                                isDuplicateCustomKey
                                                  ? "border-red-500 focus-visible:ring-red-500"
                                                  : ""
                                              }`}
                                            />
                                            {isDuplicateCustomKey && (
                                              <p className="text-[10px] text-red-500">
                                                This key already exists
                                              </p>
                                            )}
                                          </>
                                        ) : (
                                          <div className="text-xs font-medium text-foreground/80 bg-muted/50 p-2 rounded border">
                                            {mapping.key || "(empty)"}
                                          </div>
                                        )}
                                      </div>

                                      {/* Display Name - custom only */}
                                      {isCustomField && (
                                        <div className="space-y-1">
                                          <label className="text-xs text-muted-foreground">
                                            Display Name
                                          </label>
                                          <Input
                                            value={mapping.display_name || ""}
                                            onChange={(e) =>
                                              updateMapping(mapping.id, {
                                                display_name: e.target.value,
                                              })
                                            }
                                            placeholder="Enter display name"
                                            className="text-xs h-8"
                                          />
                                        </div>
                                      )}

                                      {/* Data Type */}
                                      <div className="space-y-1">
                                        <label className="text-xs text-muted-foreground">
                                          Data Type{" "}
                                          {mapping.is_required && (
                                            <span className="text-red-500">
                                              *
                                            </span>
                                          )}
                                        </label>
                                        {isCustomField ? (
                                          <Select
                                            value={
                                              mapping.data_type || "string"
                                            }
                                            onValueChange={(value) =>
                                              updateMapping(mapping.id, {
                                                data_type: value,
                                              })
                                            }
                                            disabled={!isCustomField}
                                          >
                                            <SelectTrigger
                                              className={`h-8 text-xs ${
                                                !isCustomField
                                                  ? "opacity-70 cursor-not-allowed bg-muted/50"
                                                  : ""
                                              }`}
                                            >
                                              <SelectValue placeholder="Select data type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectGroup>
                                                <SelectItem
                                                  value="string"
                                                  className="text-xs"
                                                >
                                                  String
                                                </SelectItem>
                                                <SelectItem
                                                  value="integer"
                                                  className="text-xs"
                                                >
                                                  Integer
                                                </SelectItem>
                                                <SelectItem
                                                  value="boolean"
                                                  className="text-xs"
                                                >
                                                  Boolean
                                                </SelectItem>
                                                <SelectItem
                                                  value="array"
                                                  className="text-xs"
                                                >
                                                  Array
                                                </SelectItem>
                                                <SelectItem
                                                  value="object"
                                                  className="text-xs"
                                                >
                                                  Object
                                                </SelectItem>
                                              </SelectGroup>
                                            </SelectContent>
                                          </Select>
                                        ) : (
                                          <div className="text-xs font-medium text-foreground/80 bg-muted/50 p-2 rounded border capitalize">
                                            {mapping.data_type || "string"}
                                          </div>
                                        )}
                                      </div>

                                      {/* Values Input */}
                                      <div className="space-y-1">
                                        <label className="text-xs text-muted-foreground">
                                          Value{" "}
                                          {mapping.is_required && (
                                            <span className="text-red-500">
                                              *
                                            </span>
                                          )}
                                        </label>
                                        <div className="overflow-visible">
                                          <ExpressionEditor
                                            value={mapping.values}
                                            onChange={(value) => {
                                              const rawPath = value
                                                .replace(/^\{\{/, "")
                                                .replace(/\}\}$/, "")
                                                .replace(/^\$json\./, "") // strips $json. → data[0].user.username
                                                .trim();

                                              const updates: Partial<ThreatLookupMapping> =
                                                { values: value };

                                              const inferredType =
                                                availableFieldTypeByPath.get(
                                                  rawPath,
                                                );
                                              if (inferredType) {
                                                updates.data_type =
                                                  inferredType;
                                              }

                                              updateMapping(
                                                mapping.id,
                                                updates,
                                              );
                                            }}
                                            placeholder="Type {{$ for options or enter value"
                                            availableFields={getAvailableFields}
                                            nodes={nodesForUi}
                                            currentNodeId={id}
                                            minHeight="32px"
                                            maxHeight="120px"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          )}

                          <div className="flex items-center justify-center py-5">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={addCustomMapping}
                              className="h-6 text-xs px-2"
                            >
                              <Plus size={12} className="mr-1" /> Add Custom
                              Field
                            </Button>
                          </div>
                        </div>

                        <div className="sticky -bottom-3 left-0 right-0 bg-background pb-1">
                          {(!areRequiredFieldsFilled ||
                            !action ||
                            action === "none") && (
                            <p className="text-[10px] text-orange-500 mb-2 text-center">
                              {validationHint}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 pt-2 border-t bg-background">
                    <Button
                      onClick={executeFunctionNode}
                      disabled={
                        loading ||
                        !title ||
                        !areRequiredFieldsFilled ||
                        !action ||
                        action === "none" ||
                        !isTitleValid
                      }
                      className="w-full h-9 text-xs"
                      size="sm"
                    >
                      {loading ? (
                        <>
                          <FlaskConical className="mr-2 h-4 w-4 animate-pulse" />
                          Executing...
                        </>
                      ) : (
                        <>
                          <FlaskConical className="mr-2 h-4 w-4" />
                          Execute Node
                        </>
                      )}
                    </Button>
                  </div>
                </>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Output/Response */}
            <div
              style={{ width: `${widths[2]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  loading={loading}
                  emptyMessage="Execute the function to see output preview"
                  loadingMessage="Processing..."
                  height="530px"
                  onCopy={handleCopyOutput}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}
    </FullscreenWrapper>
  );
};

export default FunctionNode;
